import type { RightWhaleState, World } from '../types';
import { NORTH_ATLANTIC_RIGHT_WHALE, estimateFor, seasonOf, stepRightWhales } from '../rightwhales';
import { RIGHT_WHALE_SUPPLY, RIGHT_WHALE_TABLE, RIGHT_WHALE_TABLE_SEASON } from '../data/rightwhales/narw';
import { STREAM, stockStream } from '../streams';
import { EXPECTED_DRAWS, stockDraws, type PopulationDraws } from '../population';
import { epochDayFromCivil, epochDayOf } from '../calendar';
import { wholeSeals } from './seals';

/** The place the right whales' stream is named for: their basin, the North Atlantic, whose two bands they live in. */
export const RIGHT_WHALE_PLACE = 'north_atlantic';

/** The right whales' draws on their population's stream of the day (`stockDraws`, one population): binomial deaths on each class's count, Poisson draws of the day's calves. */
export function rightWhaleDraws(world: { seed: number; day: number }, streamOf: typeof stockStream = stockStream): PopulationDraws {
  return stockDraws(world, STREAM.rightWhale, RIGHT_WHALE_PLACE, streamOf);
}

/** A day of the North Atlantic right whales (`rightwhales.ts`) on their own stream of the day (`migrants.ts` steps them). */
export function stepNorthAtlanticRightWhales(world: World, streamOf: typeof stockStream = stockStream): void {
  stepRightWhales(NORTH_ATLANTIC_RIGHT_WHALE, world.migrants.rightWhale, epochDayOf(world), RIGHT_WHALE_SUPPLY, rightWhaleDraws(world, streamOf));
}

/**
 * The right whales as a world opens on a day: in a season up to the table's
 * (`data/rightwhales/`, the first day of the season that opens NOAA's 2024),
 * the table scaled to NOAA's estimate for the year that season opens (TM338,
 * Table 3; its first year's before it), each class alike (M107's review: the
 * table divided back by its factor gave 672 whales in 1990, NOAA's 289);
 * after it, the table stepped on from its day. Either is stepped at expected
 * values to the opening day, each sex's classes made whole whales
 * (`wholeSeals`; no draw: a world's opening draws nothing).
 */
export function openingRightWhales(epochDay: number): RightWhaleState {
  const known = openedRightWhales.get(epochDay) ?? openRightWhales(epochDay);
  openedRightWhales.set(epochDay, known);
  return { females: [...known.females], males: [...known.males] };
}

/** The openings worked out, by day: the same for every seed, and a world years past the table's day steps every day to it (review M116: 76 ms a world in 2071, nineteen of them in one test). */
const openedRightWhales = new Map<number, RightWhaleState>();

function openRightWhales(epochDay: number): RightWhaleState {
  const life = NORTH_ATLANTIC_RIGHT_WHALE;
  const tableDay = epochDayFromCivil(RIGHT_WHALE_TABLE_SEASON, life.season.from[0], life.season.from[1]);
  const season = seasonOf(life, epochDay).year;
  const before = season <= RIGHT_WHALE_TABLE_SEASON;
  const scale = before ? (estimateFor(life, season) as number) / life.population : 1;
  const w: RightWhaleState = { females: RIGHT_WHALE_TABLE.females.map((n) => n * scale), males: RIGHT_WHALE_TABLE.males.map((n) => n * scale) };
  const from = before ? epochDayFromCivil(season, life.season.from[0], life.season.from[1]) : tableDay;
  for (let d = from; d <= epochDay; d++) stepRightWhales(life, w, d, RIGHT_WHALE_SUPPLY, EXPECTED_DRAWS);
  return { females: wholeSeals(w.females), males: wholeSeals(w.males) };
}
